'use client';

import React, { useState } from 'react';

import { IFunding, ProductModel } from '@bytel/leviator';
import {
    Align,
    Box,
    Column,
    Columns,
    Divider,
    FlexItem,
    Icon,
    IconName,
    IconSize,
    Price,
    PriceLevel,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { DiscountEnumType } from '@app-types/discount';
import { MobilePlanType } from '@app-types/plan';
import { PriceFundingsDetails } from '@app-types/product';

import { ImageThumbor } from '@components/image-thumbor';
import { InlinePrice } from '@components/inline-price';
import { ProductDetailsModal } from '@components/product-details-modal';

type PhoneInCartProps = {
    phone?: { phone: ProductModel; details?: PriceFundingsDetails };
    selectedFunding?: IFunding;
    isFloating?: boolean;
    plan: MobilePlanType;
};

export function PhoneInCart({ phone, isFloating, plan, selectedFunding }: Readonly<PhoneInCartProps>) {
    const [isPriceDetailModalOpen, setIsPriceDetailModalOpen] = useState(false);
    if (!phone || !selectedFunding) {
        return null;
    }

    const phoneAmount =
        selectedFunding.initialDeposit -
        (phone?.details?.discounts?.find((discount) => discount.type === DiscountEnumType.ODR)?.discount || 0);
    const hasOdr = phone.details?.discounts?.find((discount) => discount.type === DiscountEnumType.ODR);

    return (
        <>
            <FlexItem narrow>
                {isFloating ? (
                    <Columns align={Align.CENTER} verticalAlign={Align.CENTER} mobile>
                        <Column>
                            <Divider />
                        </Column>
                        <Column narrow>
                            <PhoneInCartBox
                                phoneInCart={phone}
                                selectedFunding={selectedFunding}
                                phoneAmount={phoneAmount}
                                setIsPriceDetailModalOpen={setIsPriceDetailModalOpen}
                                hasOdr={!!hasOdr}
                            />
                        </Column>
                        <Column>
                            <Divider />
                        </Column>
                    </Columns>
                ) : (
                    <PhoneInCartBox
                        phoneInCart={phone}
                        selectedFunding={selectedFunding}
                        phoneAmount={phoneAmount}
                        setIsPriceDetailModalOpen={setIsPriceDetailModalOpen}
                        hasOdr={!!hasOdr}
                    />
                )}
            </FlexItem>
            <FlexItem narrow>
                <View flexable fullwidth className='is-centered'>
                    <Icon size={IconSize.SMALL} name={IconName.PLUS_CIRCLE} />
                </View>
                <ProductDetailsModal
                    plan={plan}
                    currentProduct={phone}
                    onClose={() => setIsPriceDetailModalOpen(false)}
                    type={WallType.PHONE}
                    selectedFunding={selectedFunding}
                    modalOpen={!!phone && isPriceDetailModalOpen}
                />
            </FlexItem>
        </>
    );
}

interface PhoneInCartBoxProps {
    phoneInCart: { phone: ProductModel; details?: PriceFundingsDetails };
    selectedFunding?: IFunding;
    phoneAmount: number;
    setIsPriceDetailModalOpen: (open: boolean) => void;
    hasOdr: boolean;
}

export function PhoneInCartBox({
    phoneInCart: { phone },
    phoneAmount,
    selectedFunding,
    setIsPriceDetailModalOpen,
    hasOdr,
}: Readonly<PhoneInCartBoxProps>) {
    return (
        <Box>
            <Columns mobile verticalAlign={Align.CENTER}>
                <Column narrow>
                    {phone.metadata?.image && (
                        <ImageThumbor src={`${phone.metadata.image}`} alt='' width={55} height={110} />
                    )}
                </Column>
                <Column className='has-text-centered-mobile'>
                    <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.TWO}>
                        {phone.metadata?.brand} {phone.metadata?.name}
                    </Text>
                    {!!phoneAmount && (
                        <Price
                            amount={Math.max(phoneAmount, 1)}
                            level={PriceLevel.SIX}
                            className='is-fullwidth is-justified-center-mobile'
                        />
                    )}
                    {!!selectedFunding?.monthlyAmount && (
                        <Text level={TextLevels.THREE} marginless>
                            <>
                                <span>+</span>
                                <InlinePrice
                                    level={TextLevels.THREE}
                                    amount={selectedFunding?.monthlyAmount}
                                    period='mois'
                                    typo={[]}
                                />
                                <span>&nbsp;x {selectedFunding.installments} mois&nbsp;</span>
                                {!hasOdr && (
                                    <Icon
                                        onClick={() => setIsPriceDetailModalOpen(true)}
                                        name={IconName.INFOS_CIRCLE}
                                        size={IconSize.SMALLER}
                                    />
                                )}
                            </>
                        </Text>
                    )}

                    {hasOdr && (
                        <Text marginless level={TextLevels.THREE}>
                            après remboursement&nbsp;
                            <Icon
                                onClick={() => setIsPriceDetailModalOpen(true)}
                                name={IconName.INFOS_CIRCLE}
                                size={IconSize.SMALLER}
                            />
                        </Text>
                    )}
                </Column>
            </Columns>
        </Box>
    );
}
